'use client'

import { tw } from 'utils/classnames'
import { Link as WebLink } from '@repo/web-ui/components/link'

import { Link } from '@repo/design-system-cf/components/link'
import Image from 'next/image'
import { Countdown } from '@repo/web-ui/components/countdown'
import { useTranslations } from 'next-intl'
import { Ribbon } from './ribbon'
import { decodeHtmlEntities } from 'utils/decode-html-entities'

interface SimpleCardProps {
  className?: string
  productName: string
  productUrl?: string
  image: string
  alt?: string
  countdown?: number
  designerName?: string
  designerURL?: string
  linkInNewTab?: boolean
  onClick?: () => void
  category: 'fonts' | 'graphics' | 'crafts' | 'gray'
  ribbonText?: string
  button?: React.ReactNode
  categoryText?: string
  categoryUrl?: string
}

export const SimpleProductCard = ({
  button,
  className,
  productName,
  productUrl,
  image,
  alt,
  countdown,
  designerName,
  designerURL,
  linkInNewTab,
  onClick,
  category,
  ribbonText,
  categoryText,
  categoryUrl,
}: SimpleCardProps) => {
  const t = useTranslations('pages.daily-gifts.categories')

  const colorBackgroundCard = tw(category === 'gray' ? 'bg-cf-grey-200' : 'bg-cf-white')

  return (
    <div
      className={tw('group relative flex w-full flex-col rounded shadow-md', className)}
      onClick={onClick}
    >
      <div className={tw('group relative flex w-full flex-col rounded h-full')}>
        {ribbonText ? (
          <Link href={productUrl} target={linkInNewTab ? '_blank' : '_self'}>
            <Ribbon text={ribbonText} category={category} />
          </Link>
        ) : null}
        <div className="relative flex min-h-0 flex-1">
          <Link
            href={productUrl}
            target={linkInNewTab ? '_blank' : '_self'}
            className="relative inline-block h-full w-full"
          >
            <div className="bg-cf-dreamy-cloud-400 flex h-full w-full justify-center rounded-t overflow-hidden">
              <Image
                src={image}
                alt={alt || ''}
                height={274}
                width={220}
                unoptimized
                className={tw(
                  'max-h-[400px] aspect-[3/2] object-cover transition-transform duration-100 ease-in-out',
                  'h-auto w-full',
                )}
              />
            </div>
          </Link>
        </div>
        <div className={tw('items-center gap-2 rounded-b bg-white px-2 py-2', colorBackgroundCard)}>
          <div className={tw('row-start-1 flex items-center justify-between gap-[10px]')}>
            {productUrl ? (
              <WebLink
                href={productUrl}
                color="inherit"
                target={linkInNewTab ? '_blank' : '_self'}
                className="hover:text-secondary-500 overflow-hidden text-ellipsis whitespace-nowrap font-bold no-underline"
                title={productName}
              >
                <h2>{decodeHtmlEntities(productName)}</h2>
              </WebLink>
            ) : (
              <h2 className="text-14 font-bold">{decodeHtmlEntities(productName)}</h2>
            )}
          </div>
          <div className="flex items-center justify-between mt-1 flex-wrap">
            {designerName ? (
              <span className={tw('shrink-0 truncate text-xs')}>
                {t('by')}{' '}
                <Link
                  className="hover:text-secondary-500 text-blue-500 no-underline"
                  target={linkInNewTab ? '_blank' : '_self'}
                  href={designerURL}
                >
                  {decodeHtmlEntities(designerName)}
                </Link>
              </span>
            ) : null}
            {categoryText ? (
              <span className={tw('shrink-0 truncate text-xs')}>
                {t('in')}{' '}
                <Link
                  href={categoryUrl}
                  target="_blank"
                  className="hover:text-secondary-500 text-blue-500 no-underline"
                >
                  {categoryText}
                </Link>
              </span>
            ) : null}
            {countdown !== undefined ? (
              <div className="">
                <Countdown date={countdown} prefix={t('gone_in')} isEmphasized />
              </div>
            ) : null}
          </div>
          {button}
        </div>
      </div>
    </div>
  )
}
